import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import FileUpload from '../components/FileUpload.tsx';
import { createStory, getPresignedUploadUrl, getUserCharactersApi, Character } from '../services/api.ts';
import { useStories } from '../hooks/useStories.ts';
import { useSidebarStories } from '../hooks/useSidebarStories.ts';

interface CharacterSelection {
  character_id: number | null;
  name: string;
  age: number;
  image_url: string;
}

interface StorySetupData {
  child_name: string;
  story_name: string;
  age: number;
  premise: string;
  tone: string;
  art_style: string;
  scene_count: number;
  character_source: 'ai' | 'reuse';
  selected_characters: CharacterSelection[];
  user_id?: number;
}

const StorySetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refetch: refetchStories } = useStories();
  const { refetch: refetchSidebarStories } = useSidebarStories();
  const [searchParams] = useSearchParams();
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [availableCharacters, setAvailableCharacters] = useState<Character[]>([]);
  const [showCharacterPicker, setShowCharacterPicker] = useState<number | null>(null);
  const [formData, setFormData] = useState<StorySetupData>({
    child_name: '',
    story_name: '',
    age: 8,
    premise: '',
    tone: 'playful',
    art_style: 'storybook watercolor, soft and gentle',
    scene_count: 2,
    character_source: 'ai',
    selected_characters: [
      { character_id: null, name: '', age: 8, image_url: '' },
      { character_id: null, name: '', age: 8, image_url: '' }
    ]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load available characters and handle pre-selection
  useEffect(() => {
    const loadCharacters = async () => {
      try {
        const characters = await getUserCharactersApi();
        setAvailableCharacters(characters);
        
        // Handle character pre-selection from URL parameter
        const characterId = searchParams.get('character');
        if (characterId) {
          const character = characters.find(c => c.id === parseInt(characterId));
          if (character) {
            setSelectedCharacter(character);
            // Pre-populate the first character slot
            setFormData(prev => ({
              ...prev,
              character_source: 'reuse',
              selected_characters: [
                { 
                  character_id: character.id,
                  name: character.name, 
                  age: character.character_age || 8, 
                  image_url: character.image_url || ''
                },
                { character_id: null, name: '', age: 8, image_url: '' }
              ]
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching characters:', error);
      }
    };
    loadCharacters();
  }, [searchParams]);

  const toneOptions = [
    { value: 'playful', label: 'Playful' },
    { value: 'adventurous', label: 'Adventurous' },
    { value: 'magical', label: 'Magical' },
    { value: 'calm', label: 'Calm' }
  ];

  const artStyleOptions = [
    { value: 'storybook watercolor, soft and gentle', label: 'Storybook Watercolor' },
    { value: 'cartoon with smooth vector shading, clean inked outlines, soft glossy finish', label: 'Cartoon Style' },
    { value: 'illustrated with soft watercolor texture, clean pencil outlines, matte paper finish', label: 'Illustrated' },
    { value: 'realistic with natural lighting, detailed textures, photorealistic rendering', label: 'Realistic' }
  ];

  const handleInputChange = (field: keyof StorySetupData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCharacterChange = (index: number, field: keyof CharacterUpload, value: string | number | File | null) => {
    setFormData(prev => ({
      ...prev,
      uploaded_characters: prev.uploaded_characters.map((char, i) => 
        i === index ? { ...char, [field]: value } : char
      )
    }));
  };

  const handleCharacterFileUpload = async (index: number, file: File) => {
    try {
      setIsSubmitting(true);
      const response = await getPresignedUploadUrl(file.name, file.type);
      const uploadUrl = response.upload_url;
      
      // Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      // Update character with uploaded file info
      handleCharacterChange(index, 'file', file);
      handleCharacterChange(index, 'url', response.file_url);
      handleCharacterChange(index, 'filename', file.name);
      
    } catch (error) {
      console.error('Upload failed:', error);
      setErrors(prev => ({ ...prev, [`character_${index}_upload`]: 'Failed to upload image. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCharacterSourceChange = (source: 'ai' | 'reuse') => {
    setFormData(prev => ({ ...prev, character_source: source }));
    // Clear selected characters when switching to AI
    if (source === 'ai') {
      setFormData(prev => ({
        ...prev,
        selected_characters: [
          { character_id: null, name: '', age: 8, image_url: '' },
          { character_id: null, name: '', age: 8, image_url: '' }
        ]
      }));
    }
  };

  const handleCharacterSelect = (characterIndex: number, character: Character) => {
    const newCharacters = [...formData.selected_characters];
    newCharacters[characterIndex] = {
      character_id: character.id,
      name: character.name,
      age: character.character_age || 8,
      image_url: character.image_url || ''
    };
    setFormData(prev => ({ ...prev, selected_characters: newCharacters }));
    setShowCharacterPicker(null);
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.child_name.trim()) {
      newErrors.child_name = 'Child\'s name is required';
    }

    if (!formData.story_name.trim()) {
      newErrors.story_name = 'Story name is required';
    }

    if (!formData.age || formData.age < 1 || formData.age > 18) {
      newErrors.age = 'Please enter a valid age between 1 and 18';
    }

    if (!formData.premise.trim()) {
      newErrors.premise = 'Story premise is required';
    } else if (formData.premise.trim().length < 10) {
      newErrors.premise = 'Story premise must be at least 10 characters long';
    }

    if (formData.character_source === 'upload') {
      const hasValidCharacters = formData.uploaded_characters.some(char => 
        char.name.trim() && char.age > 0 && char.url
      );
      if (!hasValidCharacters) {
        newErrors.upload = 'Please upload at least one character image with name and age';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const storyData: any = {
        ...formData
      };
      
      // Only include user_id if it's actually provided
      if (user?.id) {
        storyData.user_id = user.id;
      }
      
      // Include uploaded characters if using upload source (legacy)
      if (formData.character_source === 'upload') {
        storyData.uploaded_characters = formData.uploaded_characters
          .filter(char => char.name.trim() && char.age > 0 && char.url)
          .map(char => ({
            image_url: char.url,
            name: char.name,
            age: char.age,
            filename: char.filename
          }));
      }
      
      // Include selected characters if using reuse source
      if (formData.character_source === 'reuse') {
        storyData.selected_characters = formData.selected_characters
          .filter(char => char.character_id !== null)
          .map(char => ({
            character_id: char.character_id,
            name: char.name,
            age: char.age,
            image_url: char.image_url
          }));
      }

      const response = await createStory(storyData);
      
      // Refresh both stories lists to show the new story
      await Promise.all([
        refetchStories(),
        refetchSidebarStories()
      ]);
      
      // Navigate based on character source
      if (formData.character_source === 'ai') {
        navigate(`/character-builder/${response.id}`);
      } else if (formData.character_source === 'reuse') {
        // Characters are already linked, go to character-builder to show them
        navigate(`/character-builder/${response.id}`);
      } else {
        // Legacy upload source - go to scenes after character generation
        navigate(`/scenes/${response.id}`);
      }
      
    } catch (error) {
      console.error('Failed to create story:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to create story. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/home')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Imagination Station</span>
            </button>
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-800 mb-2">
                Create Your Story
              </h1>
              <p className="text-lg text-gray-600">
                Let's set up the perfect story for your child
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
            {/* Child's Name */}
            <div className="mb-6">
              <label htmlFor="child_name" className="block text-sm font-semibold text-gray-700 mb-2">
                Child's Name *
              </label>
              <input
                type="text"
                id="child_name"
                value={formData.child_name}
                onChange={(e) => handleInputChange('child_name', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                  errors.child_name ? 'border-red-300' : 'border-gray-200'
                }`}
                placeholder="Enter your child's name"
              />
              {errors.child_name && (
                <p className="text-red-500 text-sm mt-1">{errors.child_name}</p>
              )}
            </div>

            {/* Story Name */}
            <div className="mb-6">
              <label htmlFor="story_name" className="block text-sm font-semibold text-gray-700 mb-2">
                Story Name *
              </label>
              <input
                type="text"
                id="story_name"
                value={formData.story_name}
                onChange={(e) => handleInputChange('story_name', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                  errors.story_name ? 'border-red-300' : 'border-gray-200'
                }`}
                placeholder="Enter a name for this story"
              />
              {errors.story_name && (
                <p className="text-red-500 text-sm mt-1">{errors.story_name}</p>
              )}
            </div>

            {/* Age */}
            <div className="mb-6">
              <label htmlFor="age" className="block text-sm font-semibold text-gray-700 mb-2">
                Child's Age *
              </label>
              <input
                type="number"
                id="age"
                min="1"
                max="18"
                value={formData.age}
                onChange={(e) => handleInputChange('age', parseInt(e.target.value) || 0)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                  errors.age ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              {errors.age && (
                <p className="text-red-500 text-sm mt-1">{errors.age}</p>
              )}
            </div>

            {/* Premise */}
            <div className="mb-6">
              <label htmlFor="premise" className="block text-sm font-semibold text-gray-700 mb-2">
                Story Premise *
              </label>
              <textarea
                id="premise"
                rows={4}
                value={formData.premise}
                onChange={(e) => handleInputChange('premise', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none ${
                  errors.premise ? 'border-red-300' : 'border-gray-200'
                }`}
                placeholder="Describe your story idea... (e.g., 'A brave little girl discovers a magical garden where flowers can talk')"
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.premise.length}/500 characters (minimum 10)
              </p>
              {errors.premise && (
                <p className="text-red-500 text-sm mt-1">{errors.premise}</p>
              )}
            </div>

            {/* Tone */}
            <div className="mb-6">
              <label htmlFor="tone" className="block text-sm font-semibold text-gray-700 mb-2">
                Story Tone
              </label>
              <select
                id="tone"
                value={formData.tone}
                onChange={(e) => handleInputChange('tone', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
              >
                {toneOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Art Style */}
            <div className="mb-6">
              <label htmlFor="art_style" className="block text-sm font-semibold text-gray-700 mb-2">
                Art Style
              </label>
              <select
                id="art_style"
                value={formData.art_style}
                onChange={(e) => handleInputChange('art_style', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
              >
                {artStyleOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Choose the visual style for your story characters
              </p>
            </div>

            {/* Scene Count */}
            <div className="mb-6">
              <label htmlFor="scene_count" className="block text-sm font-semibold text-gray-700 mb-2">
                Number of Scenes
              </label>
              <select
                id="scene_count"
                value={formData.scene_count}
                onChange={(e) => handleInputChange('scene_count', parseInt(e.target.value))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
              >
                <option value="1">1 Scene (Quick Story)</option>
                <option value="2">2 Scenes (Short Story)</option>
                <option value="3">3 Scenes (Medium Story)</option>
                <option value="4">4 Scenes (Long Story)</option>
                <option value="5">5 Scenes (Epic Story)</option>
                <option value="6">6 Scenes (Full Adventure)</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Choose how many scenes your story will have
              </p>
            </div>

            {/* Character Choice */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Character Choice
              </label>
              <div className="space-y-3">
                <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="character_source"
                    value="ai"
                    checked={formData.character_source === 'ai'}
                    onChange={() => handleCharacterSourceChange('ai')}
                    className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="ml-3">
                    <div className="font-medium text-gray-900">Generate with AI</div>
                    <div className="text-sm text-gray-500">Let AI create a unique character for your story</div>
                  </div>
                </label>

                <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="character_source"
                    value="reuse"
                    checked={formData.character_source === 'reuse'}
                    onChange={() => handleCharacterSourceChange('reuse')}
                    className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="ml-3">
                    <div className="font-medium text-gray-900">Reuse Existing Character</div>
                    <div className="text-sm text-gray-500">Select from your previously created characters</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Character Selection */}
            {formData.character_source === 'reuse' && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Characters</h3>
                {selectedCharacter && (
                  <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <span className="font-medium">Pre-selected character:</span> {selectedCharacter.name} 
                      {selectedCharacter.character_age && ` (age ${selectedCharacter.character_age})`}
                    </p>
                  </div>
                )}
                
                {availableCharacters.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">You don't have any characters yet.</p>
                    <button
                      onClick={() => navigate('/characters')}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Create Your First Character
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {formData.selected_characters.map((character, index) => (
                      <div key={index} className="border-2 border-gray-200 rounded-lg p-6">
                        <h4 className="text-md font-medium text-gray-700 mb-4">
                          Character {index + 1}
                        </h4>
                        
                        {/* Character Selection Button */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Character
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowCharacterPicker(index)}
                            className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-left"
                          >
                            {character.character_id ? (
                              <span className="text-gray-700">Change character...</span>
                            ) : (
                              <span className="text-gray-500">Click to select a character</span>
                            )}
                          </button>
                        </div>
                        
                        {/* Selected Character Preview */}
                        {character.character_id && character.image_url && (
                          <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                            <img
                              src={character.image_url}
                              alt={character.name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div>
                              <p className="font-medium text-gray-900">{character.name}</p>
                              <p className="text-sm text-gray-500">Age: {character.age}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Submit Error */}
            {errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-4 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 focus:ring-4 focus:ring-purple-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating Story...' : 'Create Story'}
            </button>
          </form>
        </div>
      </div>

      {/* Character Picker Modal */}
      {showCharacterPicker !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Select Character {showCharacterPicker + 1}
              </h3>
              <button
                onClick={() => setShowCharacterPicker(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh]">
              {availableCharacters.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">You don't have any characters yet.</p>
                  <button
                    onClick={() => {
                      setShowCharacterPicker(null);
                      navigate('/characters');
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Create Your First Character
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {availableCharacters.map((char) => (
                    <div
                      key={char.id}
                      onClick={() => handleCharacterSelect(showCharacterPicker, char)}
                      className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors"
                    >
                      <div className="aspect-square mb-3">
                        <img
                          src={char.image_url || '/placeholder-character.png'}
                          alt={char.name}
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </div>
                      <div className="text-center">
                        <h4 className="font-medium text-gray-900">{char.name}</h4>
                        {char.character_age && (
                          <p className="text-sm text-gray-500">Age: {char.character_age}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorySetup;
